/**
 * InstancedBatchManager — batches static platforms into InstancedMesh objects.
 *
 * v1's version keyed batches off an entity `type|shape|size`, because arenas were
 * assembled by an AI at runtime. v2 arenas are `MapDef.platforms` — boxes with a
 * `Surface` — so the batch key is `surfaceKind|size`, which collapses a
 * 200-platform arena to a handful of draw calls.
 *
 * The reason this survives the rewrite unchanged in spirit: a phone rendering
 * 200 individual platform meshes spends its whole frame budget on draw calls and
 * never gets to the characters.
 */

import * as THREE from 'three/webgpu';
import { createInstancedPlatformMaterial, panelRepeatFor, surfaceColor } from '../ToonMaterials.js';

const _tempMatrix = new THREE.Matrix4();
const _tempPosition = new THREE.Vector3();
const _tempQuaternion = new THREE.Quaternion();
const _tempScale = new THREE.Vector3(1, 1, 1);
const _tempColor = new THREE.Color();
const ZERO_MATRIX = new THREE.Matrix4().makeScale(0, 0, 0);

const INITIAL_CAPACITY = 128;
/** One unit cube, scaled per instance. Every platform in a MapDef is a box. */
let _unitBox = null;

let _scene = null;
const _batches = new Map();
const _entityToBatch = new Map();
export const instancedEntityIds = new Set();

class InstancedBatch {
  constructor(geometry, material, capacity = INITIAL_CAPACITY) {
    this.mesh = new THREE.InstancedMesh(geometry, material, capacity);
    this.mesh.castShadow = true;
    this.mesh.receiveShadow = true;
    this.mesh.frustumCulled = false;
    this.mesh.count = 0;
    this.capacity = capacity;
    this.freeSlots = [];
  }
}

export function initInstancedBatchManager(sceneRef) {
  _scene = sceneRef;
  if (!_unitBox) _unitBox = new THREE.BoxGeometry(1, 1, 1);
}

/**
 * Ice keeps its own physical material (clearcoat is the whole read) and
 * conveyors each need their own scrolling texture, so neither can share a batch
 * material. Non-solid platforms are decoration the sim ignores.
 */
export function isInstanceable(platform) {
  if (!platform.solid) return false;
  const kind = platform.surface.kind;
  return kind !== 'ice' && kind !== 'conveyor';
}

/**
 * Kind AND panel tiling.
 *
 * Batching purely by kind meant a 3-unit candle and a 9-unit island shared one
 * material, so one of them had to be wrong — and the big one always lost,
 * because a stretched texture is far more visible than a dense one. Adding the
 * tiling costs a handful of extra batches (the repeat is quantised to whole
 * numbers, so there are only ever a few) and makes a panel the same size on
 * every surface in the arena.
 */
function batchKeyFor(platform) {
  return `${platform.surface.kind}|${panelRepeatFor(Math.max(platform.hx, platform.hz) * 2)}`;
}

function getOrCreateBatch(platform) {
  const batchKey = batchKeyFor(platform);
  let batch = _batches.get(batchKey);
  if (batch) return { batch, batchKey };

  batch = new InstancedBatch(
    _unitBox,
    createInstancedPlatformMaterial(platform.surface.kind, panelRepeatFor(Math.max(platform.hx, platform.hz) * 2))
  );
  _batches.set(batchKey, batch);
  _scene.add(batch.mesh);
  return { batch, batchKey };
}

function buildMatrix(platform) {
  _tempPosition.set(platform.x, platform.y, platform.z);
  _tempQuaternion.identity();
  // Half-extents in the sim, full size on the mesh.
  _tempScale.set(platform.hx * 2, platform.hy * 2, platform.hz * 2);
  _tempMatrix.compose(_tempPosition, _tempQuaternion, _tempScale);
}

function growBatch(batch) {
  const newCapacity = batch.capacity * 2;
  const oldMesh = batch.mesh;

  const newMesh = new THREE.InstancedMesh(oldMesh.geometry, oldMesh.material, newCapacity);
  newMesh.castShadow = true;
  newMesh.receiveShadow = true;
  newMesh.frustumCulled = false;
  newMesh.count = oldMesh.count;

  for (let i = 0; i < oldMesh.count; i++) {
    oldMesh.getMatrixAt(i, _tempMatrix);
    newMesh.setMatrixAt(i, _tempMatrix);
  }
  newMesh.instanceMatrix.needsUpdate = true;

  if (oldMesh.instanceColor) {
    for (let i = 0; i < oldMesh.count; i++) {
      oldMesh.getColorAt(i, _tempColor);
      newMesh.setColorAt(i, _tempColor);
    }
    newMesh.instanceColor.needsUpdate = true;
  }

  _scene.remove(oldMesh);
  oldMesh.dispose();
  _scene.add(newMesh);

  batch.mesh = newMesh;
  batch.capacity = newCapacity;
}

export function addInstancedPlatform(platform) {
  if (instancedEntityIds.has(platform.id)) return;

  const { batch, batchKey } = getOrCreateBatch(platform);

  let index;
  if (batch.freeSlots.length > 0) {
    index = batch.freeSlots.pop();
  } else {
    if (batch.mesh.count >= batch.capacity) growBatch(batch);
    index = batch.mesh.count++;
  }

  buildMatrix(platform);
  batch.mesh.setMatrixAt(index, _tempMatrix);
  batch.mesh.instanceMatrix.needsUpdate = true;

  // Slight per-instance value variation so a 40-platform grid does not read as
  // one flat surface under cel shading.
  const color = surfaceColor(platform.surface.kind);
  color.multiplyScalar(0.9 + hashUnit(platform.id) * 0.2);
  batch.mesh.setColorAt(index, color);
  batch.mesh.instanceColor.needsUpdate = true;

  _entityToBatch.set(platform.id, { batchKey, instanceIndex: index });
  instancedEntityIds.add(platform.id);
}

export function removeInstancedPlatform(platformId) {
  const info = _entityToBatch.get(platformId);
  if (!info) return;

  const batch = _batches.get(info.batchKey);
  if (batch) {
    batch.mesh.setMatrixAt(info.instanceIndex, ZERO_MATRIX);
    batch.mesh.instanceMatrix.needsUpdate = true;
    batch.freeSlots.push(info.instanceIndex);
  }

  _entityToBatch.delete(platformId);
  instancedEntityIds.delete(platformId);
}

export function clearAllBatches() {
  for (const batch of _batches.values()) {
    _scene.remove(batch.mesh);
    // Geometry is the shared unit box — disposing it would break every batch.
    batch.mesh.material.dispose();
    batch.mesh.dispose();
  }
  _batches.clear();
  _entityToBatch.clear();
  instancedEntityIds.clear();
}

/** Stable 0..1 from an id, so colour jitter is identical on every client. */
function hashUnit(id) {
  let h = 2166136261;
  for (let i = 0; i < id.length; i++) {
    h ^= id.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return ((h >>> 0) % 1000) / 1000;
}
